// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import type {
	BadgeInventoryItemResponse,
	BadgeInventoryResponse,
	CatalogBadgeInventoryItemResponse,
} from '@fluxer/schema/src/domains/badge/BadgeResponseSchemas';
import {makeAutoObservable, runInAction} from 'mobx';
import {createContext} from 'react';
import {defaultBadgeRank, sortByBadgeOrder, toOrderPayload} from './BadgeOrder';

export type InventoryBadge = BadgeInventoryItemResponse | CatalogBadgeInventoryItemResponse;
export const inventoryBadgeKey = (badge: InventoryBadge) => (badge.source === 'catalog' ? badge.badge_id : badge.key);
const endpoint = `${Endpoints.USER_ME}/badges`;

export class BadgeInventoryState {
	badges: Array<BadgeInventoryItemResponse> = [];
	catalog: Array<CatalogBadgeInventoryItemResponse> = [];
	order: Array<string> = [];
	nextCursor: string | null = null;
	loading = false;
	loaded = false;
	pending = new Set<string>();
	private savedOrder: Array<string> = [];
	// One write at a time: a visibility write racing an order write fails the user row's version check.
	private tail: Promise<void> | null = null;
	private queuedOrder: Promise<void> | null = null;

	constructor(readonly userId: string) {
		makeAutoObservable<this, 'savedOrder' | 'tail' | 'queuedOrder'>(
			this,
			{savedOrder: false, tail: false, queuedOrder: false},
			{autoBind: true},
		);
	}

	get items(): Array<InventoryBadge> {
		const byDefault = [...this.badges, ...this.catalog].sort(
			(a, b) => defaultBadgeRank(inventoryBadgeKey(a)) - defaultBadgeRank(inventoryBadgeKey(b)),
		);
		return sortByBadgeOrder(byDefault, inventoryBadgeKey, this.order);
	}

	async loadAll(): Promise<void> {
		if (this.loading || (this.loaded && !this.nextCursor)) return;
		this.loading = true;
		try {
			do {
				const response = await http.get<BadgeInventoryResponse>(endpoint, {
					query: {after: this.nextCursor ?? undefined, limit: 100},
				});
				runInAction(() => {
					// Built-in visibility and order may have changed while a catalog page was in flight.
					if (!this.loaded) {
						this.badges = response.body.badges;
						this.order = response.body.order ?? [];
						this.savedOrder = [...this.order];
					}
					const known = new Set(this.catalog.map((badge) => badge.badge_id));
					this.catalog.push(...response.body.catalog.filter((badge) => !known.has(badge.badge_id)));
					this.nextCursor = response.body.next_cursor;
					this.loaded = true;
				});
			} while (this.nextCursor);
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	move(from: number, to: number): Promise<void> {
		const keys = this.items.map(inventoryBadgeKey);
		const [key] = keys.splice(from, 1);
		keys.splice(to, 0, key);
		this.order = toOrderPayload(keys);
		return this.saveOrder();
	}

	saveOrder(): Promise<void> {
		if (this.queuedOrder) return this.queuedOrder;
		const idle = this.tail === null;
		const request = this.enqueue(this.sendOrder);
		if (!idle) this.queuedOrder = request;
		return request;
	}

	private async sendOrder(): Promise<void> {
		this.queuedOrder = null;
		const badges = [...this.order];
		try {
			await http.patch(`${endpoint}/reorder`, {body: {badges}});
		} catch (error) {
			runInAction(() => {
				this.order = [...this.savedOrder];
			});
			throw error;
		}
		this.savedOrder = badges;
		// Loaded on demand: the profile badges import this file, and the profile commands import the profile UI.
		const UserProfileCommands = await import('@app/features/user/commands/UserProfileCommands');
		UserProfileCommands.invalidate(this.userId);
	}

	async setVisible(badge: InventoryBadge, visible: boolean): Promise<void> {
		const key = inventoryBadgeKey(badge);
		if (this.pending.has(key)) return;
		const previous = badge.visible;
		badge.visible = visible;
		this.pending.add(key);
		try {
			await this.enqueue(async () => {
				await http.patch(endpoint, {body: {badge: key, visible}});
			});
		} catch (error) {
			runInAction(() => {
				badge.visible = previous;
			});
			throw error;
		} finally {
			runInAction(() => {
				this.pending.delete(key);
			});
		}
	}

	private enqueue(task: () => Promise<void>): Promise<void> {
		const request = this.tail ? this.tail.then(task) : task();
		const settled = request.then(
			() => undefined,
			() => undefined,
		);
		this.tail = settled;
		void settled.then(() => {
			if (this.tail === settled) this.tail = null;
		});
		return request;
	}
}

export const BadgeInventoryContext = createContext<BadgeInventoryState | null>(null);
